// Semantic chart spec → Highcharts options. The backend never sends raw
// Highcharts config — only this fixed vocabulary — so every option here is
// authored client-side (no config injection surface).
import { colors } from '@/theme/foundations/colors';
import type { DashboardChart, DashboardSection } from '../../../types';

// Digit grouping is RESOLVED BY THE BACKEND (chart.number_format): the
// recruiter's explicit ask ("show in lakhs" / "US format") or the benchmark
// currency's native convention — the FE just renders it. lakh_crore →
// en-IN's native 28,00,000 grouping; thousands → 2,800,000. Payloads from
// before the field existed fall back to the viewer's locale.
export const formatAmount = (n: number, numberFormat?: string): string => {
  if (numberFormat === 'lakh_crore') return n.toLocaleString('en-IN');
  if (numberFormat === 'thousands') return n.toLocaleString('en-US');
  return n.toLocaleString();
};

const FONT = 'Inter, system-ui, sans-serif';

// Highcharts wants a color string, not a CSS var — tokens resolve to rgba here.
const withAlpha = (hex: string, alpha: number): string => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CHART_COLORS = {
  series: colors['primary-500'],
  seriesFill: withAlpha(colors['primary-500'], 0.25),
  median: colors['primary-900'],
  outer: colors['secondary-400'],
  current: colors['status-3'],
  recommended: colors['pastel-2-dark'],
  recommendedFill: withAlpha(colors['pastel-2-dark'], 0.25),
  background: colors['base-white'],
  axisLine: colors['secondary-200'],
  gridLine: colors['secondary-50'],
};

const PALETTE = [
  colors['primary-500'],
  colors['status-4'],
  colors['status-3'],
  colors['pastel-7-dark'],
  colors['pastel-3-dark'],
  colors['secondary-600'],
];

const base = {
  // Solid white, NOT 'transparent': the exporting menu's "View in full
  // screen" paints the browser's fullscreen backdrop behind the chart —
  // which is black — so a transparent chart becomes invisible. The card
  // the chart sits in is white anyway, so inline rendering is unchanged
  // (and exported PNGs stop coming out transparent too).
  chart: {
    style: { fontFamily: FONT },
    backgroundColor: CHART_COLORS.background,
  },
  title: { text: null },
  credits: { enabled: false },
  legend: { enabled: false },
  colors: PALETTE,
};

// Null when the section carries no chart or an unknown chart type — the caller
// drops the whole section (header included) on null.
export const chartOptionsFor = (
  section: DashboardSection
): Record<string, unknown> | null =>
  section.chart ? dashboardChartOptions(section.chart) : null;

export const dashboardChartOptions = (
  spec: DashboardChart
): Record<string, unknown> | null => {
  switch (spec.chartType) {
    case 'bar':
    case 'column':
      return {
        ...base,
        chart: { ...base.chart, type: spec.chartType, height: 220 },
        xAxis: {
          categories: spec.categories ?? [],
          lineColor: CHART_COLORS.axisLine,
        },
        yAxis: {
          title: { text: spec.unit ?? null },
          gridLineColor: CHART_COLORS.gridLine,
        },
        series: (spec.series ?? []).map((s) => ({
          type: spec.chartType,
          name: s.name,
          data: s.data,
        })),
        plotOptions: {
          series: { dataLabels: { enabled: true }, colorByPoint: true },
        },
      };
    case 'stacked_bar':
      return {
        ...base,
        chart: { ...base.chart, type: 'bar', height: 220 },
        xAxis: { categories: spec.categories ?? [] },
        yAxis: { title: { text: spec.unit ?? null } },
        legend: { enabled: true },
        plotOptions: { series: { stacking: 'normal' } },
        series: (spec.series ?? []).map((s) => ({
          type: 'bar',
          name: s.name,
          data: s.data,
        })),
      };
    case 'donut':
      return {
        ...base,
        chart: { ...base.chart, type: 'pie', height: 240 },
        legend: { enabled: true },
        plotOptions: {
          pie: { innerSize: '60%', dataLabels: { enabled: true } },
        },
        series: [
          {
            type: 'pie',
            name: spec.series?.[0]?.name ?? '',
            data: (spec.categories ?? []).map((c, i) => ({
              name: c,
              y: spec.series?.[0]?.data?.[i] ?? 0,
            })),
          },
        ],
      };
    case 'range':
      return rangeOptions(spec);
    default:
      return null;
  }
};

// Percentile range chart: horizontal columnrange for the P25–P75 IQR, scatter
// markers for P50 (median) and P90, dashed columnrange overlay for the
// current band, solid overlay for the recommended band.
const rangeOptions = (spec: DashboardChart): Record<string, unknown> => {
  const ranges = spec.ranges ?? [];
  const cats = ranges.map((r) => r.label);
  const iqr = ranges.map((r) => [r.p25, r.p75]);
  const medians = ranges.map((r, i) => ({ x: i, y: r.p50 }));
  const p90s = ranges.map((r, i) => ({ x: i, y: r.p90 }));

  const series: Record<string, unknown>[] = [
    {
      type: 'columnrange',
      name: 'P25–P75',
      data: iqr,
      color: CHART_COLORS.seriesFill,
      borderColor: CHART_COLORS.series,
      borderWidth: 1,
      pointWidth: 26,
    },
    {
      type: 'scatter',
      name: 'Median (P50)',
      data: medians,
      color: CHART_COLORS.median,
      marker: { symbol: 'diamond', radius: 6 },
    },
    {
      type: 'scatter',
      name: 'P90',
      data: p90s,
      color: CHART_COLORS.outer,
      marker: { symbol: 'circle', radius: 4 },
    },
  ];

  const currents = ranges
    .map((r, i) =>
      r.currentMax ? { x: i, low: r.currentMin ?? 0, high: r.currentMax } : null
    )
    .filter(Boolean);
  if (currents.length > 0) {
    series.push({
      type: 'columnrange',
      name: 'Current band',
      data: currents,
      color: 'transparent',
      borderColor: CHART_COLORS.current,
      borderWidth: 2,
      dashStyle: 'Dash',
      pointWidth: 34,
    });
  }
  const recs = ranges
    .map((r, i) =>
      r.recMax ? { x: i, low: r.recMin ?? 0, high: r.recMax } : null
    )
    .filter(Boolean);
  if (recs.length > 0) {
    series.push({
      type: 'columnrange',
      name: 'Recommended',
      data: recs,
      color: CHART_COLORS.recommendedFill,
      borderColor: CHART_COLORS.recommended,
      borderWidth: 2,
      pointWidth: 18,
    });
  }

  const unit = spec.unit ? `${spec.unit} ` : '';
  return {
    ...base,
    chart: { ...base.chart, inverted: true, height: 120 + ranges.length * 60 },
    legend: { enabled: true },
    xAxis: { categories: cats, lineColor: CHART_COLORS.axisLine },
    yAxis: {
      title: { text: spec.unit ?? null },
      gridLineColor: CHART_COLORS.gridLine,
      labels: {
        formatter(this: { value: number | string }) {
          return typeof this.value === 'number'
            ? formatAmount(this.value, spec.numberFormat)
            : String(this.value);
        },
      },
    },
    // Named values, not raw x/y coordinates: the default scatter tooltip
    // reads "x: 0 · y: 225 000", which is meaningless to a recruiter.
    tooltip: {
      formatter(this: {
        series: { name: string };
        point: { low?: number; high?: number; y?: number };
      }) {
        const fmt = (n?: number) =>
          typeof n === 'number'
            ? unit + formatAmount(n, spec.numberFormat)
            : '';
        if (typeof this.point.high === 'number') {
          return `<b>${this.series.name}</b>: ${fmt(this.point.low)} – ${fmt(this.point.high)}`;
        }
        return `<b>${this.series.name}</b>: ${fmt(this.point.y)}`;
      },
    },
    series,
  };
};
